import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const pct = (v) => Math.round((v ?? 0) * 1000) / 10;
const fmt = (n) => `€${(n ?? 0).toFixed(2)}`;

export default function CashbackAdminPanel() {
  const [ov, setOv] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/cashback-overview");
      setOv(data);
      const s = data.settings;
      setForm({ margin_share_pct: pct(s.margin_share_pct), validity_months: s.validity_months, max_order_pct: pct(s.max_order_pct) });
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/admin/cashback-settings", { margin_share_pct: Number(form.margin_share_pct) / 100, validity_months: Number(form.validity_months), max_order_pct: Number(form.max_order_pct) / 100 });
      toast.success("Impostazioni cashback salvate"); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const downloadCsv = async () => {
    const { data } = await api.get("/admin/cashback-audit.csv", { responseType: "blob" });
    const url = URL.createObjectURL(data); const a = document.createElement("a"); a.href = url; a.download = "cashback_audit.csv"; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="cashback-admin-panel" className="border border-neutral-200 rounded-2xl p-4 space-y-4">
      <div><h2 className="font-display font-bold">Cashback (1 livello)</h2><p className="text-xs text-neutral-500">Il cashback è una quota del tuo margine ENBISSY (10%), accreditata solo su ordini pagati; lo sconto al checkout è a carico di ENBISSY, il venditore incassa sempre la quota piena.</p></div>
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-xl bg-neutral-50 p-3"><p className="text-xs text-neutral-500">Accreditato</p><p data-testid="cb-adm-accrued" className="font-display font-extrabold">{fmt(ov?.accrued)}</p></div>
        <div className="rounded-xl bg-neutral-50 p-3"><p className="text-xs text-neutral-500">Speso (sconti)</p><p data-testid="cb-adm-spent" className="font-display font-extrabold">{fmt(ov?.spent)}</p></div>
        <div className="rounded-xl bg-[#E0B94B]/15 p-3"><p className="text-xs text-neutral-500">Credito aperto</p><p data-testid="cb-adm-open" className="font-display font-extrabold">{fmt(ov?.open_credit)}</p></div>
        <div className="rounded-xl bg-neutral-50 p-3"><p className="text-xs text-neutral-500">Stornato</p><p className="font-display font-bold">{fmt(ov?.reversed)}</p></div>
        <div className="rounded-xl bg-neutral-50 p-3"><p className="text-xs text-neutral-500">Scaduto</p><p className="font-display font-bold">{fmt(ov?.expired)}</p></div>
        <div className="rounded-xl bg-neutral-50 p-3"><p className="text-xs text-neutral-500">Utenti</p><p className="font-display font-bold">{ov?.users ?? 0}</p></div>
      </div>
      {form && (
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-2">
            {[["margin_share_pct", "Cashback % del margine ENBISSY"], ["validity_months", "Validità credito (mesi)"], ["max_order_pct", "Max copertura ordine %"]].map(([k, l]) => (
              <div key={k}><label className="text-[11px] text-neutral-500">{l}</label><Input data-testid={`cb-set-${k}`} type="number" step="0.1" min="0" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="h-9" /></div>
            ))}
          </div>
          <p data-testid="cb-adm-example" className="text-[11px] text-neutral-500">Esempio: ordine 100 € → margine 10 € → credito {fmt(10 * Number(form.margin_share_pct || 0) / 100)} all'acquirente, ENBISSY netto {fmt(10 - 10 * Number(form.margin_share_pct || 0) / 100)}.</p>
          <div className="flex gap-2">
            <Button data-testid="cb-settings-save" onClick={save} disabled={busy} className="flex-1 h-10 rounded-full bg-black text-white">{busy ? "…" : "Salva impostazioni"}</Button>
            <Button data-testid="cb-audit-csv" onClick={downloadCsv} variant="outline" className="h-10 rounded-full"><Download size={15} className="mr-1" />Log CSV</Button>
          </div>
        </div>
      )}
    </div>
  );
}
